import React, { createContext, useState, useEffect, useCallback } from "react";

export const AppContext = createContext();

const isBrowser = typeof window !== "undefined";

const getStoredUser = () => {
  if (!isBrowser) {
    return {
      email: null,
      username: null,
      isSuperuser: false,
      isAdmin: false,
      isVendedor: false,
      isLoggedIn: false,
    };
  }

  const access = localStorage.getItem("access");
  const email = localStorage.getItem("user_email");
  const username = localStorage.getItem("user_name");
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isAdmin = localStorage.getItem("is_admin") === "true";
  const isVendedor = localStorage.getItem("is_vendedor") === "true";
  const isLoggedIn = localStorage.getItem("is_logged_in") === "true" && !!access;

  return {
    email: email || null,
    username: username || null,
    isSuperuser,
    isAdmin,
    isVendedor,
    isLoggedIn,
  };
};

const initialUser = getStoredUser();

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(initialUser);
  const [accessToken, setAccessToken] = useState(
    isBrowser ? localStorage.getItem("access") || "" : ""
  );
  const [refreshToken, setRefreshToken] = useState(
    isBrowser ? localStorage.getItem("refresh") || "" : ""
  );
  const [sections, setSections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    if (!isBrowser) return;
    const storedCart = localStorage.getItem("cartItems");
    if (storedCart) setCartItems(JSON.parse(storedCart));
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  const openDrawer = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsCartOpen((prev) => !prev);
  }, []);

  const loginUser = useCallback(
    ({ email, username, isSuperuser, isAdmin, isVendedor, access, refresh }) => {
      const nextAccess = isBrowser
        ? access ?? localStorage.getItem("access") ?? ""
        : access ?? "";
      const nextRefresh = isBrowser
        ? refresh ?? localStorage.getItem("refresh") ?? ""
        : refresh ?? "";

      if (nextAccess) {
        if (isBrowser) localStorage.setItem("access", nextAccess);
        setAccessToken(nextAccess);
      }

      if (nextRefresh) {
        if (isBrowser) localStorage.setItem("refresh", nextRefresh);
        setRefreshToken(nextRefresh);
      }

      const payload = {
        email: email || null,
        username: username || null,
        isSuperuser: !!isSuperuser,
        isAdmin: !!isAdmin,
        isVendedor: !!isVendedor,
        isLoggedIn: true,
      };

      setUser(payload);

      if (isBrowser) {
        localStorage.setItem("user_email", payload.email ?? "");
        localStorage.setItem("user_name", payload.username ?? "");
        localStorage.setItem("is_superuser", String(payload.isSuperuser));
        localStorage.setItem("is_admin", String(payload.isAdmin));
        localStorage.setItem("is_vendedor", String(payload.isVendedor));
        localStorage.setItem("is_logged_in", "true");
      }
    },
    []
  );

  const logoutUser = useCallback(() => {
    setUser({
      email: null,
      username: null,
      isSuperuser: false,
      isAdmin: false,
      isVendedor: false,
      isLoggedIn: false,
    });
    setAccessToken("");
    setRefreshToken("");
    setSections([]);
    setBrands([]);
    setCartItems([]);
    setIsCartOpen(false);

    if (!isBrowser) return;

    localStorage.removeItem("user_email");
    localStorage.removeItem("user_name");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("is_vendedor");
    localStorage.removeItem("is_logged_in");
    localStorage.removeItem("cartItems");
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (!isBrowser) return null;

    const storedRefresh = localStorage.getItem("refresh");

    if (!storedRefresh) {
      logoutUser();
      return null;
    }

    try {
      const response = await fetch("/api/token/refresh/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: storedRefresh }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.access) {
        logoutUser();
        return null;
      }

      localStorage.setItem("access", data.access);
      setAccessToken(data.access);

      if (data.refresh) {
        localStorage.setItem("refresh", data.refresh);
        setRefreshToken(data.refresh);
      }

      return data.access;
    } catch {
      logoutUser();
      return null;
    }
  }, [logoutUser]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      let token = isBrowser ? localStorage.getItem("access") || "" : "";

      const headers = new Headers(options.headers || {});

      if (token) headers.set("Authorization", `Bearer ${token}`);

      let response = await fetch(url, { ...options, headers });

      if (response.status === 401 && isBrowser) {
        const newAccess = await refreshAccessToken();

        if (!newAccess) return response;

        const retryHeaders = new Headers(options.headers || {});
        retryHeaders.set("Authorization", `Bearer ${newAccess}`);

        response = await fetch(url, {
          ...options,
          headers: retryHeaders,
        });
      }

      return response;
    },
    [refreshAccessToken]
  );

  useEffect(() => {
    const validateSession = async () => {
      if (!isBrowser) {
        setAuthLoading(false);
        return;
      }

      const token = localStorage.getItem("access");

      if (!token) {
        setAuthLoading(false);
        logoutUser();
        return;
      }

      try {
        const res = await fetch("/sistema/get-user-info/", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          logoutUser();
          return;
        }

        const data = await res.json();

        loginUser({
          email: data.email,
          username: data.username,
          isSuperuser: data.is_superuser,
          isAdmin: data.is_admin,
          isVendedor: data.is_vendedor,
        });
      } catch {
        logoutUser();
      } finally {
        setAuthLoading(false);
      }
    };

    validateSession();
  }, [loginUser, logoutUser]);

  const addToCart = (product, quantity) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);

      if (existing) {
        return prev.map((i) =>
          i.id === product.id
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }

      return [...prev, { ...product, quantity }];
    });

    setIsCartOpen(true);
  };

  const removeFromCart = (id) =>
    setCartItems((prev) => prev.filter((i) => i.id !== id));

  const clearCart = () => setCartItems([]);

  const refreshSections = useCallback(async () => {
    if (!user.isLoggedIn) return;

    setSectionsLoading(true);

    try {
      const response = await authFetch("/categorias/secciones/");

      if (!response.ok) {
        setSections([]);
        return;
      }

      const data = await response.json();
      setSections(Array.isArray(data) ? data : []);
    } catch {
      setSections([]);
    } finally {
      setSectionsLoading(false);
    }
  }, [authFetch, user.isLoggedIn]);

  const refreshBrands = useCallback(async () => {
    if (!user.isLoggedIn) return;

    setBrandsLoading(true);

    try {
      const response = await authFetch("/categorias/marcas/");

      if (!response.ok) {
        setBrands([]);
        return;
      }

      const data = await response.json();
      setBrands(Array.isArray(data) ? data : []);
    } catch {
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  }, [authFetch, user.isLoggedIn]);

  useEffect(() => {
    if (!user.isLoggedIn) return;
    refreshSections();
    refreshBrands();
  }, [refreshSections, refreshBrands, user.isLoggedIn]);

  return (
    <AppContext.Provider
      value={{
        user,
        setUser,
        loginUser,
        logoutUser,
        accessToken,
        refreshToken,
        setAccessToken,
        setRefreshToken,
        authLoading,
        sections,
        setSections,
        brands,
        setBrands,
        refreshSections,
        refreshBrands,
        sectionsLoading,
        brandsLoading,
        cartItems,
        setCartItems,
        addToCart,
        removeFromCart,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        csrfFetch: authFetch,
        authFetch,
        refreshAccessToken,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};